// Backend API types (lowercase as per API spec)
// Must stay in sync with apps/positions/models/position.py STATUS_CHOICES. No
// close-side settlement status by design (LR-611): closing is one step. A new member
// also needs OPEN_BOOK_STATUSES and map-server-params.LIVE_STATUSES — plain string
// arrays that tsc will NOT flag for you (the Record maps below it will).
export type TradeStatus =
  | 'pending'
  | 'pending_open_settlement'   // LR-292: TD hold posted, awaiting SD
  | 'active'
  | 'matured'
  | 'closed'
  | 'cancelled'
  | 'rejected';                 // LR-408: BO rejected N times — operator must /retry/ or cancel

// LR-497: two settlement statuses the trader-facing model surfaces that are not stored
// COLUMNS — they are DERIVED per-row from fields the API already returns:
//   • partially_settled          — FOP opening, exactly one leg settled
//   • pending_partial_settlement — active trade with a pending partial recall/return
// Derived on BOTH sides (getEffectiveTradeStatus and, since LR-546, the server's
// `effective_status` annotation), so both codes are valid `?statuses=` values.
// This widened set is what the Status filter, badge, detail pill and chips key on.
export type EffectiveTradeStatus = TradeStatus | 'partially_settled' | 'pending_partial_settlement';

// LR-376: positions in these statuses are terminal — no operator action
// remains. They are hidden from the default /trades view and surface in
// the History tab instead. `matured` and `rejected` are NOT terminal
// (still need operator follow-up: set close date / retry-or-cancel).
export const TERMINAL_TRADE_STATUSES = ['closed', 'cancelled'] as const;
export type TerminalTradeStatus = (typeof TERMINAL_TRADE_STATUSES)[number];

// Statuses that represent a real OPEN (or opening/closing) book — the position
// has an actual BO hold / transfer, so it carries genuine value & exposure.
// `pending` (not yet opened) and `rejected` (BO never opened it) carry NO economic
// exposure, so they must be excluded from the metric money totals even though they
// stay visible in the /trades blotter (flagged by the Attention chip and the
// amber row accent, LR-686) for operator follow-up.
// Mirrors the MTM page's row filter (MtmPage `rows`).
export const OPEN_BOOK_STATUSES = [
  'active',
  'matured',
  'pending_open_settlement',
] as const;
export const isOpenBookStatus = (status: string): boolean =>
  (OPEN_BOOK_STATUSES as readonly string[]).includes(status);

// LR-408/LR-544: BO hold retry ceiling — the hourly cron promotes a pending
// position to `rejected` after this many failed BO holds. Kept in lock-step with
// Position.BO_MAX_RETRIES on the backend; the single FE source for the blotter
// "retry N/max" chip and the pending banner's "Retry N of max" copy.
export const BO_MAX_RETRIES = 3;

// LR-497: canonical human-readable settlement-status labels — the single FE
// source of truth for the Status badge, detail pill, sidebar filter dropdown and
// the applied-filter chips (modelled on COLLATERAL_TYPE_LABELS below). Kept in
// lock-step with Position.STATUS_CHOICES on the backend for the stored statuses; the two
// derived ones are computed on both sides — see the EffectiveTradeStatus note above.
export const TRADE_STATUS_LABELS: Record<EffectiveTradeStatus, string> = {
  pending: 'Pending Trade Date',
  pending_open_settlement: 'Pending Settlement',
  partially_settled: 'Partially Settled',
  active: 'Active',
  pending_partial_settlement: 'Pending Partial Settlement',
  matured: 'Matured',
  closed: 'Closed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

// Full lifecycle context for the badge/pill hover tooltip. Keyed by the widened
// EffectiveTradeStatus so the derived states get their own explanation.
export const STATUS_TOOLTIPS: Record<EffectiveTradeStatus, string> = {
  pending: 'Created — awaiting Trade Date',
  pending_open_settlement: 'Hold placed, awaiting settlement',
  // Required for type-completeness of the Record; in the trades-list cell the
  // richer inline tooltip (`Partially Settled · Cash: … · Securities: …`)
  // supersedes this, so this exact string only surfaces if a future consumer
  // reads STATUS_TOOLTIPS without that override.
  partially_settled: 'FOP: one leg settled, awaiting the other',
  active: 'Trade settled, accruing earnings',
  pending_partial_settlement: 'Partial recall/return pending settlement',
  matured: 'Reached maturity, awaiting close flow',
  closed: 'Position fully closed',
  cancelled: 'Cancelled before settlement',
  rejected: 'Rejected by BO after max retries',
};
export type FrameworkAgreement = 'gmsla' | 'gmra';
export type TradeType = 'lend' | 'borrow' | 'repo' | 'reverse_repo';
export type CollateralType = 'cash' | 'non_cash' | 'uncollateralized';

/**
 * Human-readable labels for CollateralType — single source of truth for the
 * Trades/MTM table cell, the sidebar filter dropdown, and the applied-filter
 * chips (LR-444). Keep in lock-step with the CollateralType enum above.
 */
export const COLLATERAL_TYPE_LABELS: Record<CollateralType, string> = {
  cash: 'Cash',
  non_cash: 'Non-Cash',
  uncollateralized: 'Uncollateralized',
};
export type DeliveryType = 'dvp' | 'fop';
export type SettlementVenue = 'dtc' | 'euroclear';
export type BenchmarkRate = 'sofr' | 'obfr' | 'sonia' | 'estr' | 'tonar' | 'honia' | 'other';
export type DayCountConvention = '360' | '365';

/**
 * A benchmark reference rate served by GET /api/v1/references/benchmarks/
 * (LR-435). Replaces the previously-hardcoded BENCHMARK_RATES tables. `rate`
 * is a percent (e.g. 4.3 for 4.30%).
 */
export interface BenchmarkApiItem {
  name: BenchmarkRate;
  currency_default: string;
  rate: number;
  as_of: string;
  /**
   * Display-ready Bloomberg ticker (LR-443), e.g. 'BBG SOFRRATE' for
   * SOFRRATE.INDEX — rendered verbatim. Null for benchmarks not sourced from
   * TickDB.
   */
  bbg_ticker?: string | null;
}
export type RateType = 'floating' | 'fixed';
export type RateEntryMode = 'rebate' | 'fee';
export type SecurityType = 'stock' | 'bond';
export type TermType = 'open' | 'evergreen' | 'term';
export type PreDeliveryParty = 'us' | 'counterparty' | 'simultaneous';

/**
 * Table section for grouped display
 */
export interface TableSection {
  id: string;
  title: string;
  items: TradeItem[];
}

/**
 * Discriminated union for virtualized display rows
 */
export type DisplayRow =
  | { type: 'section-header'; sectionId: string; title: string; count: number }
  | { type: 'trade'; item: TradeItem };

// Legacy types for backward compatibility (to be phased out)
export type AgreementStatus = 'ACTIVE' | 'EXPIRED' | 'PENDING_APPROVAL' | 'TERMINATED';

export type RateLabel = 'Rebate Rate';
export type AgreementType = 'gmra' | 'gmsla';

export const GMRA_TRADE_TYPES: TradeType[] = ['repo', 'reverse_repo'];

/**
 * Every bookable leg. A counterparty may hold both master agreements (LR-634),
 * so Trade Entry offers all four regardless of the counterparty registry.
 * `readonly`: both trade-form hooks hand this very array out as
 * `availableTradeTypes`, not a per-render copy.
 */
export const ALL_TRADE_TYPES: readonly TradeType[] = ['lend', 'borrow', 'repo', 'reverse_repo'];

/**
 * Uniform "Rebate Rate" across all agreements (LR-453: GMRA repo dropped the
 * legacy "Repo Rate" label). Kept as a function so call sites stay unchanged.
 */
export function getRateLabel(_tradeType: TradeType): RateLabel {
  return 'Rebate Rate';
}

/**
 * Derive the framework agreement type from trade_type.
 */
export function getAgreementType(tradeType: TradeType): AgreementType {
  return GMRA_TRADE_TYPES.includes(tradeType) ? 'gmra' : 'gmsla';
}

/**
 * Human-readable labels for the `trade_reason` enum. Single source of truth:
 * useCreateTradeForm builds its dropdown TRADE_REASON_OPTIONS from this map, and
 * the trade-detail Terms tab renders via formatTradeReason() below.
 */
export const TRADE_REASON_LABELS: Record<string, string> = {
  SPECIAL: 'Special (High Demand)',
  GENERAL_COLLATERAL: 'General Collateral',
  CORPORATE_ACTION: 'Corporate Action',
  SHORT_COVERING: 'Short Covering',
  MARKET_MAKING: 'Market Making',
  CLIENT_SERVICE: 'Client Service',
  INTERNAL: 'Internal Transfer',
  OTHER: 'Other',
};

/**
 * Render a backend `trade_reason` as a human label. NB: `trade_reason` is a
 * free-text CharField on the backend, NOT a constrained enum (LR-208 r6 LOW doc
 * fix) — its canonical values come from TRADE_REASON_OPTIONS but any string can
 * arrive, and case is inconsistent ('client_service' vs 'INTERNAL'). The key is
 * upper-cased before lookup; unknown/arbitrary values are prettified (snake/upper
 * → Title Case) rather than shown raw.
 */
export function formatTradeReason(raw: string): string {
  const key = raw.trim().toUpperCase();
  return (
    TRADE_REASON_LABELS[key] ??
    raw
      .trim()
      .toLowerCase()
      .split(/[\s_]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/**
 * Position List Item - matches backend PositionList schema
 * Used for list views with minimal fields
 */
/**
 * LR-709/LR-711: the trade's latest UNRESOLVED BackOffice posting attempt.
 *
 * The structured successor to the free-text `last_bo_error`: it says WHICH posting
 * (op/leg), in what state, and since when. `null` is the normal case — BackOffice's
 * last word was clean — so a non-null value already means "this trade needs
 * someone", which is why the blotter can raise its signal on the presence of the
 * object alone.
 *
 * The list and the detail payloads share these keys; detail adds the BackOffice
 * response an operator reads (`BoPostingStateDetail`), which has no place in a row.
 */
export interface BoPostingState {
  /** rejected — BO judged the payload and refused it (the payload is wrong).
   *  failed — BO was unreachable or errored (nothing booked; retry is safe).
   *  timed_out — the outcome is unknown: BO MAY have booked it.
   *  confirmed_unrecorded — BO DID book it and our local write was lost, so the
   *  trade does not reflect a booking that exists there (LR-709 round 2). The one
   *  state where "may have booked it" would understate what we know. */
  state: 'rejected' | 'failed' | 'timed_out' | 'confirmed_unrecorded';
  op: 'hold' | 'release' | 'transfer_open' | 'transfer_close' | 'transfer_recall' | 'transfer_mtm';
  leg: 'securities' | 'collateral';
  attempted_at: string | null;
  /** Always true on a value that is present at all — the backend only sends
   *  unresolved failures. Kept so a caller can branch without knowing that. */
  is_unresolved_failure: boolean;
}

/**
 * LR-711: what `POST /positions/{id}/bo-posting/reconcile/` answers.
 *
 * `outcomes` counts what happened to each of the trade's blocked postings:
 *   unblocked — stamped with a request id, cleared for an automatic re-post;
 *   recorded  — already in our ledger; the block was stale and is gone;
 *   legacy    — predates the request id, so only a person who has checked
 *               BackOffice can settle it. It is still blocked;
 *   error     — resolving THAT block raised; the others were still processed,
 *               which is why the call answers 200 and reports it here rather
 *               than failing the whole request.
 */
export interface BoPostingReconcileResult {
  outcomes: Record<string, number>;
  position: PositionDetail;
}

/**
 * LR-715: what `POST /positions/{id}/bo-posting/settle-manually/` answers.
 *
 * `settled` — the posting rows the operator closed on their own authority.
 * `still_blocked` — of those, the ones whose posting GUARD is still on. Two
 *   separate facts with two separate authorities: closing the row says the books
 *   agree, releasing the guard says posting again is allowed, and only the admin
 *   Release action does the second. It matters here because the settled row takes
 *   the panel away with it — so this list is the operator's only notice that the
 *   trade still refuses further postings.
 */
export interface BoPostingSettleResult {
  settled: string[];
  still_blocked: string[];
  position: PositionDetail;
}

/** The detail-page view: everything above, plus what there is room to read. */
export interface BoPostingStateDetail extends BoPostingState {
  /** For a timed_out attempt: read_timeout | unreadable_2xx | worker_died. */
  reason?: string;
  http_status?: number | null;
  /** BackOffice's verbatim refusal ("account not found") — the operator's text. */
  response_body?: string;
  value_date?: string;
  attempt_id?: string;
  /** BackOffice's own id for the booking, once one is known (LR-710). */
  external_id?: string;
  /**
   * Does the posting guard still hold, or has it already been cleared?
   *
   * Not the same question as the state. The attempt log is append-only, so a
   * `timed_out` attempt stays `timed_out` until BackOffice's answer is actually
   * learned — which happens on the next scheduled posting run, not the moment the
   * guard is released. Between those two points the trade is "cleared, waiting",
   * not "stuck", and only this flag says so. `undefined` (an older payload) is
   * read as still blocked, which is the conservative side.
   */
  is_blocked?: boolean;
  /**
   * LR-706 (g): was this posting sent before `extraData.requestId` existed?
   *
   * The fact that decides which exit the posting has:
   *
   *   stamped     — Reconcile. The re-POST is deduplicated by BackOffice, and its
   *                 own answer settles the row.
   *   legacy      — Record from BackOffice. A repeat cannot be recognised, so we
   *                 never re-POST: we READ what BackOffice holds and write our
   *                 side from it. LR-715's manual close stands beside it for the
   *                 operator who has already put BackOffice right by hand — the
   *                 two are alternatives, not the same door twice.
   *
   * It matters to the copy as much as to the action: on a legacy posting the
   * sentence "reconciling sends it again under the same request id" is false —
   * there is no request id — and Reconcile answers `legacy` and changes nothing.
   *
   * The same fact as `has_request_id` inverted, and one of the two should go —
   * LR-724. Two tickets named it independently; nothing but the name differs.
   */
  is_legacy?: boolean;
  /**
   * LR-715: has BackOffice since booked the request id this posting was written
   * from?
   *
   * Splits the two ways a `confirmed_unrecorded` posting sits here with its guard
   * already cleared — identical on the row, opposite in meaning:
   *
   *   false — the re-post has not been answered yet. The 409 closes it on its own;
   *           there is nothing for a person to do but wait.
   *   true  — the re-post was answered *200*: BackOffice booked an id it had
   *           already accepted. Its deduplication is global and never expires, so
   *           that answer contradicts its own contract and what is left standing is
   *           that it holds this posting TWICE. Nothing it says later can close the
   *           row — only a person who has reconciled the books there.
   *
   * The manual settlement is offered on `true` alone. `undefined` (an older
   * payload) reads as false, which is the conservative side: the action is
   * irreversible and admin-only.
   */
  is_contradicted?: boolean;
  /**
   * Was this posting stamped with an `extraData.requestId`? (LR-715 round 2)
   *
   * The fact that says whether `is_contradicted` can ever become true. Proof of a
   * contradiction is a later `confirmed` row under the same request id; a posting
   * sent before LR-710 carries none, so `is_contradicted` stays false forever.
   * `false` here therefore means "no answer can settle this row" — not "wait for
   * one" — and it is the population LR-713's read-back creates.
   */
  has_request_id?: boolean;
}

/**
 * LR-706 (g): what `GET /positions/{id}/bo-posting/read-back/` answers — the rows
 * BackOffice holds for this trade's legacy posting, as the operator sees them
 * before deciding.
 *
 * `bo_amount` beside `our_amount` on purpose: a pending trade can be amended
 * between the posting BackOffice kept and this moment, and the local row will
 * record BackOffice's figure. The difference belongs in front of the person
 * approving it, not in a log afterwards.
 */
export interface BoPostingReadBackTransaction {
  uuid: string;
  account: string;
  asset: string;
  leg: string;
  bo_amount: string;
  our_amount: string;
  value_date: string;
}

export interface BoPostingReadBack {
  op: string;
  leg: string;
  uuids: string[];
  /** BackOffice ids already present in our book — nothing left to record. */
  already_recorded: string[];
  transactions: BoPostingReadBackTransaction[];
}

/** What `POST /positions/{id}/bo-posting/record-from-backoffice/` answers. */
export interface BoPostingRecordResult {
  uuids: string[];
  transactions_created: number;
  position: PositionDetail;
}

export interface PositionListItem {
  id: number;
  status: TradeStatus;
  status_display: string;
  symbol_id: string;
  description: string;
  isin: string;
  currency: string;
  trade_type: TradeType;
  trade_type_display: string;
  agreement: FrameworkAgreement;
  agreement_display: string;
  collateral_type?: CollateralType;
  /** LR-586: pool holding this trade's cash collateral; null for non-cash. */
  cash_pool?: number | null;
  cash_pool_name?: string | null;
  counterparty_name: string;
  counterparty_id: number;
  custodian_name: string | null;
  custodian_account?: string;
  counterparty_account?: string;
  booking_center?: string;
  quantity: number;
  /** LR-388: pending partial recall/return qty booked against the trade. */
  pending_recall_quantity?: number;
  /** LR-388: MTM-markable remaining shape = quantity − pending_recall_quantity. */
  remaining_quantity?: number;
  /** LR-703: earliest contractual settlement date among the PENDING recalls/returns
   *  booked on this trade (list payload only); null when none, or none carries a date. */
  pending_recall_settlement_date?: string | null;
  gross_price: string;
  /** COB (T-1 close from TickDB daily candles). Null when no candle is
   *  available for the symbol — frontend falls back to gross_price for
   *  prefill, dash for display (LR-393). */
  cob_price?: string | null;
  /** COB (T-1 close) FX rate, security → settlement ccy (LR-494). Null for
   *  same-ccy trades or when no rate resolves; FE falls back to booked fx_rate. */
  cob_fx_rate?: string | null;
  /** LR-521: COB margin price + gross/margin values derived from cob_price
   *  (cob_price × margin, × qty, × qty × margin). Security-ccy; *_converted are
   *  the settlement-ccy twins (at the COB FX rate). Null when cob_price is
   *  unavailable / same-ccy for the converted twins. */
  cob_margin_price?: string | null;
  cob_margin_price_converted?: string | null;
  cob_gross_value?: string | null;
  cob_gross_value_converted?: string | null;
  cob_margin_value?: string | null;
  cob_margin_value_converted?: string | null;
  current_market_price: string | null;
  /** LR-532: when current_market_price was last pulled/refreshed (ISO 8601, UTC).
   *  Null until a price is first set at trade creation; stays on the last
   *  successful pull for symbols that had no fresh quote. */
  current_market_price_updated_at?: string | null;
  margin_price: string | null;
  contract_value: string | null;
  margin: string;
  rate_entry_mode?: RateEntryMode;
  // LR-539: the list serializer now emits rate_type + benchmark_rate so the bulk
  // re-rate R7 gate (fixed → no spread) and the preview build-up work off the
  // blotter row without a detail fetch. Decimal arrives as a string (COERCE).
  rate_type?: RateType;
  benchmark_rate?: string | null;
  benchmark_name?: BenchmarkRate;
  total_rate?: string;
  /** LR-628: true when the nightly accrual — not the booking — owns this
   *  benchmark, i.e. a GMSLA floating rebate on a benchmark that publishes a
   *  fixing. It is NOT derivable from a null `effective_*` (a fed benchmark
   *  whose feed is down looks identical) and it is what decides whether the
   *  re-rate form may offer a benchmark field at all. Repo, fee mode, fixed
   *  rates and the feedless benchmarks (TONAR/HONIA/OTHER) stay false and keep
   *  a re-rateable benchmark. */
  benchmark_follows_fixing?: boolean;
  /** LR-628: what a floating rebate accrues TODAY — the benchmark resolved from
   *  the day's fixing, plus the derived total and daily earnings. Null unless
   *  the trade actually floats on a published fixing, so every consumer reads
   *  `effective_x ?? x`: `benchmark_rate` / `total_rate` remain the BOOKING
   *  snapshot (audit trail, re-rate "before" values, amend payload). Displaying
   *  the booked pair on a floating trade is what made the trade page disagree
   *  with its own statement. */
  effective_benchmark_rate?: string | null;
  effective_total_rate?: string | null;
  effective_daily_earnings?: string | null;
  effective_daily_earnings_converted?: string | null;
  /** LR-631: the cost leg on today's POOL rate — see the detail twin. */
  effective_cost_of_collateral?: string | null;
  /** `net_amount` on the rate the effective Daily Earnings uses; null when there is none. */
  effective_net_amount?: string | null;
  spread_bps?: number | null;
  fee_percent?: string | null;
  day_count_convention?: DayCountConvention | null;
  daily_earnings: string;
  // Non-cash collateral
  collateral_isin?: string;
  collateral_symbol_id?: string;
  collateral_quantity?: string | null;
  collateral_percent?: string;
  collateral_value?: string | null;
  pre_delivery_party?: PreDeliveryParty | '';
  collateral_settlement_place?: number | null;
  collateral_settlement_place_name?: string | null;
  cost_of_collateral?: string | null;
  fee_income?: string | null;
  net_amount?: string | null;
  last_marked_price?: string | null;
  /** Raw latest mark price; null when the position has never been marked (no
   *  current_market_price fallback). Retained for the "Last Marked" column;
   *  no longer fed into the New Price prefill (LR-393). */
  latest_mark_price?: string | null;
  closing_cash?: string | null;
  closing_cash_projected?: boolean;
  /** Closing cash in settlement currency (LR-349); null for same-currency trades. */
  closing_cash_converted?: string | null;
  /** Margin price / contract value / expected daily return in settlement currency
   *  (LR-459); null for same-currency trades. */
  margin_price_converted?: string | null;
  contract_value_converted?: string | null;
  daily_earnings_converted?: string | null;
  /** LR-522: Trade Gross Value = gross_price × quantity; *_converted is the
   *  settlement-ccy twin (booked fx_rate; null for same-ccy). */
  trade_gross_value?: string | null;
  trade_gross_value_converted?: string | null;
  /** LR-476: margin price / contract value re-derived from the latest mark
   *  (qty × mark × margin_decimal). Display-only — booking margin_price /
   *  contract_value stay canonical. LR-522: null until the position is first
   *  marked (blank Last Marked stage); repo/reverse never marked. *_converted
   *  is the settlement-ccy twin. */
  marked_margin_price?: string | null;
  marked_margin_price_converted?: string | null;
  marked_contract_value?: string | null;
  marked_contract_value_converted?: string | null;
  /** LR-522: Last Marked Gross Value = latest_mark_price × quantity; null until
   *  first marked. *_converted is the settlement-ccy twin. */
  last_marked_gross_value?: string | null;
  last_marked_gross_value_converted?: string | null;
  /** Cash collateral a recall/return or the close gives a share of, in the currency of the
   *  trade's cash leg in BO: what it holds there plus the MTM transfers still on their way. A
   *  recall/return of N gives back N / quantity of it, the close the rest. Null when
   *  uncollateralized, for a cross-currency trade without fx_rate, or when the BO cash is on the
   *  wrong side for the trade. */
  collateral_to_return?: string | null;
  rate_label?: RateLabel;
  agreement_type?: AgreementType;
  term_type?: TermType;
  term_type_display?: string;
  trade_date: string;
  settlement_date: string;
  maturity_date: string | null;
  notice_period_days?: number | null;
  notice_served?: boolean;
  notice_served_date?: string | null;
  notice_served_by?: string | null;
  actual_settlement_date?: string | null;
  delivery_type?: DeliveryType;
  settlement_venue?: SettlementVenue;
  cash_settlement_place?: number | null;
  securities_settlement_place?: number | null;
  cash_settlement_place_name?: string | null;
  securities_settlement_place_name?: string | null;
  cash_settled?: boolean;
  securities_settled?: boolean;
  cash_settled_at?: string | null;
  securities_settled_at?: string | null;
  settlement_method?: 'manual' | 'automatic';
  settlement_currency?: string;
  /** Booked FX rate of the trade, security → settlement ccy (LR-474). Decimal
   *  string like the other money fields; "1.000000" for same-currency trades. */
  fx_rate?: string;
  // LR-408: BO failure tracking. last_bo_error reads as a JSON-ish blob with
  // BO's `description` keyed by payload index.
  bo_retry_count?: number;
  /**
   * @deprecated LR-712 — read `bo_posting` instead, via `resolveBoReason`
   * (utils/trades/bo-posting-copy). One overwritable string for the whole trade:
   * it names whichever posting failed LAST, not the one that stalled the trade,
   * and it is cleared wholesale by any later success. Still served, and still the
   * only record for a trade rejected before LR-709 shipped the posting log —
   * which is the one case `resolveBoReason` falls back to it.
   */
  last_bo_error?: string;
  // LR-711: the structured posting state, on the list path too — this is what the
  // blotter's stuck-posting signal reads. null = BackOffice's last word was clean.
  bo_posting?: BoPostingState | null;
  created_at: string;
  updated_at: string;
}

/**
 * Position Detail - matches backend PositionDetail schema
 * Used for detail views with all fields including calculated ones
 */
/**
 * LR-470: a booked partial recall (lend side) / return (borrow side) row on a
 * position. Pending rows reduce the MTM-markable remaining quantity; a manual
 * settle click then reduces the booked quantity and posts a partial reverse
 * transfer to Backoffice. settlement_date is the contractual reference date —
 * there is no auto-settle when it is reached.
 */
export interface PartialRecall {
  id: number;
  kind: 'recall' | 'return';
  kind_display: string;
  quantity: number;
  status: 'pending' | 'settled' | 'cancelled';
  status_display: string;
  booked_date?: string | null;
  /** Contractual settlement date (overridable while pending); null = settle manually. */
  settlement_date?: string | null;
  settled_at?: string | null;
  settled_by?: string | null;
  created_at: string;
}

export interface PositionDetail {
  id: number;
  status: TradeStatus;
  status_display: string;
  symbol_id: string;
  description: string;
  sedol?: string;
  isin: string;
  security_type?: SecurityType;
  country?: string;
  currency: string;
  booking_center?: string;
  custodian_account?: string;
  counterparty_account?: string;
  counterparty_name: string;
  counterparty_id: number;
  agreement: FrameworkAgreement;
  agreement_display: string;
  trade_type: TradeType;
  trade_type_display: string;
  collateral_type?: CollateralType;
  /** LR-586: pool holding this trade's cash collateral; null for non-cash. */
  cash_pool?: number | null;
  cash_pool_name?: string | null;
  /** LR-631: the pool's day-count convention ('360' | '365'). The basis the cost
   *  leg is priced on - the amend preview runs no registry effects and has no
   *  other way to know it. */
  cash_pool_day_count?: string | null;
  collateral_type_display?: string;
  quantity: number;
  available_quantity?: number;
  /** LR-388: pending partial recall/return qty + the resulting markable remaining shape. */
  pending_recall_quantity?: number;
  remaining_quantity?: number;
  /** LR-470: booked partial recalls/returns (newest first) for the detail panel. */
  partial_recalls?: PartialRecall[];
  gross_price: string;
  margin: string;
  margin_decimal: string;
  margin_bps: number;
  margin_price?: string;
  contract_value?: string;
  rate_entry_mode?: RateEntryMode;
  rate_type?: RateType;
  rate_type_display?: string;
  benchmark_name?: BenchmarkRate;
  benchmark_rate?: string;
  benchmark_locked?: boolean;
  benchmark_lock_date?: string | null;
  spread_bps?: number | null;
  fee_percent?: string | null;
  day_count_convention?: DayCountConvention | null;
  total_rate?: string;
  /** LR-628: true when the nightly accrual — not the booking — owns this
   *  benchmark, i.e. a GMSLA floating rebate on a benchmark that publishes a
   *  fixing. It is NOT derivable from a null `effective_*` (a fed benchmark
   *  whose feed is down looks identical) and it is what decides whether the
   *  re-rate form may offer a benchmark field at all. Repo, fee mode, fixed
   *  rates and the feedless benchmarks (TONAR/HONIA/OTHER) stay false and keep
   *  a re-rateable benchmark. */
  benchmark_follows_fixing?: boolean;
  /** LR-628: what a floating rebate accrues TODAY — the benchmark resolved from
   *  the day's fixing, plus the derived total and daily earnings. Null unless
   *  the trade actually floats on a published fixing, so every consumer reads
   *  `effective_x ?? x`: `benchmark_rate` / `total_rate` remain the BOOKING
   *  snapshot (audit trail, re-rate "before" values, amend payload). Displaying
   *  the booked pair on a floating trade is what made the trade page disagree
   *  with its own statement. */
  effective_benchmark_rate?: string | null;
  effective_total_rate?: string | null;
  effective_daily_earnings?: string | null;
  effective_annual_earnings?: string | null;
  effective_daily_earnings_converted?: string | null;
  /** LR-631: the cost leg on today's POOL rate. Detail only — the trade page nets
   *  Fee − Cost against an effective Net/day, so the booked cost would leave the
   *  decomposition not adding up. `fee_income` has no twin on purpose: nothing
   *  reprices the fee the operator typed. */
  effective_cost_of_collateral?: string | null;
  trade_reason?: string;
  dividend_passthrough_percent?: string;
  // LR-208 r6 LOW: nullable — the backend property returns None when the rate
  // base is missing (and in fee mode when net_amount is null), so the field can
  // serialize as null. Consumers already route through the null-tolerant decimal
  // helpers; typing it honestly forces a compile-time guard on any new consumer.
  daily_earnings: string | null;
  annual_earnings: string;
  cost_of_collateral?: string | null;
  fee_income?: string | null;
  net_amount?: string | null;
  last_marked_price?: string | null;
  /** Raw latest mark price; null when the position has never been marked (no
   *  current_market_price fallback). See PositionListItem.latest_mark_price. */
  latest_mark_price?: string | null;
  /** COB (T-1 close from TickDB daily candles). See PositionListItem.cob_price. */
  cob_price?: string | null;
  /** COB (T-1 close) FX rate, security → settlement ccy. See PositionListItem.cob_fx_rate. */
  cob_fx_rate?: string | null;
  /** LR-521: COB margin price + gross/margin values (+ settlement-ccy twins).
   *  See PositionListItem for semantics. */
  cob_margin_price?: string | null;
  cob_margin_price_converted?: string | null;
  cob_gross_value?: string | null;
  cob_gross_value_converted?: string | null;
  cob_margin_value?: string | null;
  cob_margin_value_converted?: string | null;
  closing_cash?: string | null;
  closing_cash_projected?: boolean;
  /** Closing cash in settlement currency (LR-349); null for same-currency trades. */
  closing_cash_converted?: string | null;
  /** Margin price / contract value / expected daily return in settlement currency
   *  (LR-459); null for same-currency trades. */
  margin_price_converted?: string | null;
  contract_value_converted?: string | null;
  daily_earnings_converted?: string | null;
  /** LR-522: Trade Gross Value = gross_price × quantity; *_converted is the
   *  settlement-ccy twin (booked fx_rate; null for same-ccy). */
  trade_gross_value?: string | null;
  trade_gross_value_converted?: string | null;
  /** LR-476: margin price / contract value re-derived from the latest mark
   *  (qty × mark × margin_decimal). Display-only — booking margin_price /
   *  contract_value stay canonical. LR-522: null until the position is first
   *  marked (blank Last Marked stage); repo/reverse never marked. *_converted
   *  is the settlement-ccy twin. */
  marked_margin_price?: string | null;
  marked_margin_price_converted?: string | null;
  marked_contract_value?: string | null;
  marked_contract_value_converted?: string | null;
  /** LR-522: Last Marked Gross Value = latest_mark_price × quantity; null until
   *  first marked. *_converted is the settlement-ccy twin. */
  last_marked_gross_value?: string | null;
  last_marked_gross_value_converted?: string | null;
  /** Cash collateral a recall/return or the close gives a share of, in the currency of the
   *  trade's cash leg in BO: what it holds there plus the MTM transfers still on their way. A
   *  recall/return of N gives back N / quantity of it, the close the rest. Null when
   *  uncollateralized, for a cross-currency trade without fx_rate, or when the BO cash is on the
   *  wrong side for the trade. */
  collateral_to_return?: string | null;
  rate_label?: RateLabel;
  agreement_type?: AgreementType;
  term_type?: TermType;
  term_type_display?: string;
  trade_date: string;
  settlement_date: string;
  maturity_date: string | null;
  notice_period_days?: number | null;
  notice_served?: boolean;
  notice_served_date?: string | null;
  notice_served_by?: string | null;
  delivery_type?: DeliveryType;
  settlement_venue?: SettlementVenue;
  settlement_location?: string;
  cash_settlement_place?: number | null;
  securities_settlement_place?: number | null;
  cash_settlement_place_name?: string | null;
  securities_settlement_place_name?: string | null;
  cash_settled?: boolean;
  securities_settled?: boolean;
  cash_settled_at?: string | null;
  securities_settled_at?: string | null;
  settlement_method?: 'manual' | 'automatic';
  settlement_currency?: string;
  fx_rate?: string;
  // Non-cash collateral
  collateral_isin?: string;
  collateral_symbol_id?: string;
  collateral_quantity?: string | null;
  collateral_cob_price?: string | null;
  collateral_current_market_price?: string | null;
  collateral_percent?: string;
  collateral_value?: string | null;
  pre_delivery_party?: PreDeliveryParty | '';
  pre_delivery_party_display?: string;
  collateral_settlement_place?: number | null;
  collateral_settlement_place_name?: string | null;
  actual_settlement_date: string | null;
  close_settlement_date?: string | null;
  settled_by?: string | null;
  days_to_maturity: string;
  // LR-208 r6 LOW: the backend serializes these via @extend_schema_field(BooleanField)
  // returning model booleans, i.e. JSON true/false — not strings. (days_to_maturity
  // stays string: it ships as a number but the sole consumer coerces via String().)
  is_evergreen: boolean;
  is_evergreen_active?: boolean;
  is_evergreen_fixed?: boolean;
  has_fixed_maturity?: boolean;
  is_near_maturity: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  notes?: string;
  // LR-408/LR-544: BO failure tracking, surfaced in the pending banner on the
  // detail page (already present on PositionListItem/TradeItem).
  bo_retry_count?: number;
  /** @deprecated LR-712 — see the note on `PositionListItem.last_bo_error`. */
  last_bo_error?: string;
  // LR-709/LR-711: the same posting state as the row, plus the BackOffice response
  // an operator reads. null = nothing unresolved.
  bo_posting?: BoPostingStateDetail | null;
}

/**
 * TradeItem - Frontend representation for compatibility
 * Maps to PositionListItem with camelCase for UI components
 */
export interface TradeItem {
  id: number;
  status: TradeStatus;
  status_display: string;
  symbol_id: string;
  description: string;
  isin: string;
  counterparty: string;
  counterpartyId: number;
  agreement: FrameworkAgreement;
  agreement_display: string;
  trade_type: TradeType;
  trade_type_display: string;
  collateral_type?: CollateralType;
  /** LR-586: pool holding this trade's cash collateral; null for non-cash. */
  cash_pool?: number | null;
  cash_pool_name?: string | null;
  quantity: number;
  /** LR-388: pending partial recall/return qty booked against the trade (0/undefined = none). */
  pending_recall_quantity?: number;
  /** LR-388: server-computed MTM-markable remaining shape (quantity − pending_recall_quantity).
   *  effectiveQuantity() returns this when present, so MTM marks only the net remaining. */
  remaining_quantity?: number;
  /** LR-703: earliest settlement date among the PENDING recalls/returns — the row is
   *  flagged once it is past (settling is a manual click, so nothing chases it). */
  pending_recall_settlement_date?: string | null;
  gross_price: number;
  /** API-original decimal string; precision-safe for bulk_mark API contract (Phase 4/5). */
  gross_price_raw: string;
  /** COB price (T-1 close from TickDB daily candles), parsed to number for UI math. */
  cob_price?: number | null;
  /** API-original decimal string of cob_price; preserves trailing-zero precision
   *  (e.g. "152.50"). Source of `resolveDefaultPrice` for the MTM New Price prefill
   *  and the COB Price (T-1) column (LR-393). */
  cob_price_raw?: string | null;
  /** API-original string for last_marked_price; preserves trailing-zero precision (e.g. "152.50"). */
  last_marked_price_raw?: string | null;
  /** API-original string of the latest MTM mark; null when never marked. Used
   *  by `mtmBaselinePriceRaw` as the "Δ since last mark" baseline in the
   *  exposure bar. NOT used for the New Price prefill — see resolveDefaultPrice
   *  (LR-393: prefill switched to cob_price_raw to avoid cyclic dependency). */
  latest_mark_price_raw?: string | null;
  current_market_price: number | null;
  /** LR-532: ISO 8601 (UTC) timestamp of the last current_market_price pull;
   *  kept as a string (display-only, formatted in the cell tooltip). */
  current_market_price_updated_at?: string | null;
  margin_price: number | null;
  contract_value: number;
  margin: number;
  rate_entry_mode?: RateEntryMode;
  // LR-539: rate_type + benchmark_rate feed the bulk re-rate R7 gate (fixed → no
  // spread) and the current-rate build-up / Set-rate default in the staging grid.
  rate_type?: RateType;
  benchmark_rate?: number | null;
  benchmark_name?: BenchmarkRate;
  total_rate: number | null;
  /** LR-628: true when the nightly accrual — not the booking — owns this
   *  benchmark, i.e. a GMSLA floating rebate on a benchmark that publishes a
   *  fixing. It is NOT derivable from a null `effective_*` (a fed benchmark
   *  whose feed is down looks identical) and it is what decides whether the
   *  re-rate form may offer a benchmark field at all. Repo, fee mode, fixed
   *  rates and the feedless benchmarks (TONAR/HONIA/OTHER) stay false and keep
   *  a re-rateable benchmark. */
  benchmark_follows_fixing?: boolean;
  /** LR-628: what a floating rebate accrues TODAY — the benchmark resolved from
   *  the day's fixing, plus the derived total and daily earnings. Null unless
   *  the trade actually floats on a published fixing, so every consumer reads
   *  `effective_x ?? x`: `benchmark_rate` / `total_rate` remain the BOOKING
   *  snapshot (audit trail, re-rate "before" values, amend payload). Displaying
   *  the booked pair on a floating trade is what made the trade page disagree
   *  with its own statement. */
  effective_benchmark_rate?: number | null;
  effective_total_rate?: number | null;
  effective_daily_earnings?: number | null;
  effective_daily_earnings_converted?: number | null;
  /** LR-631: the cost leg on today's POOL rate. The Daily Earnings cell beside it
   *  is already effective, and for a fee trade that figure IS fee minus this. */
  effective_cost_of_collateral?: number | null;
  effective_net_amount?: number | null;
  spread_bps?: number | null;
  fee_percent: number | null;
  daily_earnings: number | null;
  cost_of_collateral?: number | null;
  fee_income?: number | null;
  net_amount?: number | null;
  last_marked_price?: number | null;
  /** LR-306/522: parsed raw latest MTM mark — null when never marked (no
   *  current_market_price fallback). Drives the "Last Marked Gross Price" column
   *  (honest dash when unmarked); also the strict "MTM-or-gross_price" fallback
   *  in getTradeTotalValue (MetricsBar), distinct from `last_marked_price` which
   *  the backend masks with current_market_price for exposure / market-value math. */
  latest_mark_price?: number | null;
  closing_cash?: number | null;
  closing_cash_projected?: boolean;
  /** Closing cash in settlement currency (LR-349); null for same-currency trades. */
  closing_cash_converted?: number | null;
  /** Settlement-currency money metrics (LR-459); null for same-currency trades. */
  margin_price_converted?: number | null;
  contract_value_converted?: number | null;
  daily_earnings_converted?: number | null;
  /** LR-522: Trade Gross Value = gross_price × quantity; *_converted settlement twin. */
  trade_gross_value?: number | null;
  trade_gross_value_converted?: number | null;
  /** LR-476: marked (post-MtM) display twins — margin price / contract value
   *  re-derived from the latest mark. Display-only; booking margin_price /
   *  contract_value stay canonical for exposure/adequacy/accrual. LR-522: null
   *  until the position is first marked (blank Last Marked stage). */
  marked_margin_price?: number | null;
  marked_margin_price_converted?: number | null;
  marked_contract_value?: number | null;
  marked_contract_value_converted?: number | null;
  /** LR-522: Last Marked Gross Value = latest_mark_price × quantity; null until
   *  first marked. *_converted settlement twin. */
  last_marked_gross_value?: number | null;
  last_marked_gross_value_converted?: number | null;
  /** Cash collateral a recall/return or the close gives a share of, in the currency of the
   *  trade's cash leg in BO: what it holds there plus the MTM transfers still on their way. A
   *  recall/return of N gives back N / quantity of it, the close the rest. Null when
   *  uncollateralized, for a cross-currency trade without fx_rate, or when the BO cash is on the
   *  wrong side for the trade. */
  collateral_to_return?: number | null;
  settlement_currency?: string;
  /** Booked FX rate, security → settlement ccy (LR-474); null when the API
   *  payload predates the field. 1 for same-currency trades. */
  fx_rate?: number | null;
  /** COB (T-1 close) FX rate, security → settlement ccy (LR-494); parsed to
   *  number. Null for same-ccy trades or when TickDB has no rate — the COB FX
   *  column then falls back to the booked fx_rate. */
  cob_fx_rate?: number | null;
  /** LR-521: COB margin price + gross/margin values (cob_price × margin, × qty,
   *  × qty × margin), parsed to number. Security-ccy; *_converted are the
   *  settlement-ccy twins (at the COB FX rate). Null when cob_price unavailable /
   *  same-ccy for the converted twins. */
  cob_margin_price?: number | null;
  cob_margin_price_converted?: number | null;
  cob_gross_value?: number | null;
  cob_gross_value_converted?: number | null;
  cob_margin_value?: number | null;
  cob_margin_value_converted?: number | null;
  rate_label?: RateLabel;
  agreement_type?: AgreementType;
  trade_date: string;
  settlement_date: string;
  maturity_date: string | null;
  notice_period_days?: number | null;
  notice_served?: boolean;
  notice_served_date?: string | null;
  notice_served_by?: string | null;
  actual_settlement_date?: string | null;
  delivery_type?: DeliveryType;
  settlement_venue?: SettlementVenue;
  cash_settlement_place?: number | null;
  securities_settlement_place?: number | null;
  cash_settlement_place_name?: string | null;
  securities_settlement_place_name?: string | null;
  cash_settled?: boolean;
  securities_settled?: boolean;
  cash_settled_at?: string | null;
  securities_settled_at?: string | null;
  settlement_method?: 'manual' | 'automatic';
  // LR-408: BO failure tracking — surfaced as Rejected badge / pending retry-counter / tooltip.
  bo_retry_count?: number;
  /** @deprecated LR-712 — see the note on `PositionListItem.last_bo_error`. */
  last_bo_error?: string;
  // LR-711: a BackOffice posting that never resolved — the row accent + tooltip.
  bo_posting?: BoPostingState | null;
  custodian_name: string | null;
  custodian_account?: string;
  counterparty_account?: string;
  booking_center?: string;
  currency: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  term_type?: TermType;
  term_type_display?: string;
  // Non-cash collateral
  collateral_isin?: string;
  collateral_symbol_id?: string;
  collateral_quantity?: number | null;
  collateral_percent?: number;
  collateral_value?: number | null;
  pre_delivery_party?: PreDeliveryParty | '';
  collateral_settlement_place?: number | null;
  collateral_settlement_place_name?: string | null;
}

/**
 * Query parameters for listing positions
 * Matches backend API query params
 */
export interface TradeListParams {
  // Legacy singular params (still honoured by the backend for back-compat).
  agreement?: FrameworkAgreement;
  collateral_type?: CollateralType;
  counterparty?: string;
  isin?: string;
  symbol_id?: string;
  status?: TradeStatus;
  trade_type?: TradeType;
  trade_date_from?: string;
  trade_date_to?: string;
  maturity_date_from?: string;
  maturity_date_to?: string;
  limit?: number;
  offset?: number;
  // LR-546: server-side blotter query surface. `search` = DRF SearchFilter;
  // `ordering` = FE column id (e.g. '-quantity', 'status_display'); the plural
  // params are CSV multi-select; *_min/_max / *_from/_to are ranges. Names match
  // the backend PositionFilter exactly (see map-server-params.ts).
  search?: string;
  ordering?: string;
  statuses?: string;
  /** CP filter by FK id (CSV, comma-safe). The FE always scopes by id — the backend
   *  also accepts a `counterparties` name CSV for other clients, but it is deliberately
   *  NOT surfaced here (a name with a comma splits wrong). */
  counterparty_ids?: string;
  symbols?: string;
  trade_types?: string;
  collateral_types?: string;
  agreement_types?: string;
  term_types?: string;
  currencies?: string;
  settlement_currencies?: string;
  settlement_date_from?: string;
  settlement_date_to?: string;
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
  quantity_min?: number;
  quantity_max?: number;
  contract_value_min?: number;
  contract_value_max?: number;
  gross_price_min?: number;
  gross_price_max?: number;
  margin_min?: number;
  margin_max?: number;
  /** Keyset cursor: rows with a greater id. The CSV export pages with it under `ordering=id`. */
  id_gt?: number;
  /** Upper bound on id: the export fixes the newest trade at its start and leaves later bookings out. */
  id_lte?: number;
}

/**
 * LR-546: per-currency money subtotal, keyed by ISO code -> decimal string
 * (e.g. `{ USD: '15000.00', EUR: '5000.00' }`). The frontend rolls these up to
 * the display base with LIVE FX (describeCurrencyTotal), so the backend returns
 * per-currency subtotals rather than a single base-currency number.
 */
export type MoneySummary = Record<string, string>;

/** LR-546: per-counterparty aggregates from `GET /positions/metrics/`. */
export interface CounterpartySummary {
  counterparty_id: number | null;
  counterparty_name: string | null;
  count: number;
  attention_count: number;
  active_count: number;
  stock_lb: {
    count: number;
    open_count: number;
    total_value_lent: MoneySummary;
    total_value_borrowed: MoneySummary;
    exposure_lent: MoneySummary;
    exposure_borrowed: MoneySummary;
  };
  repo: {
    count: number;
    open_count: number;
    total_value: MoneySummary;
    net_exposure: MoneySummary;
  };
}

/** Per-effective-settlement-ccy aggregate for the MetricsBar CurrencyFilterPill.
 *  `unconvertible` = a cross-ccy bucket with no usable booked fx_rate (pill ⚠). */
export interface SettlementCcyTotal {
  ccy: string;
  count: number;
  /** Σ settlement Total Value, decimal string in `ccy`. */
  value: string;
  unconvertible: boolean;
}

/** LR-700: per-term-type counts for the MetricsBar TermFilterPill. Server-ordered
 *  (Open → Term → Evergreen, then anything unexpected) — render as received; a
 *  term type with no rows in the slice is ABSENT, not present with count 0. */
export interface TermTypeTotal {
  term_type: string;
  count: number;
}

/** LR-546: whole-book blotter metrics + counts (`GET /positions/metrics/`). */
export interface PositionsSummary {
  overall: { count: number; attention_count: number; active_count: number };
  settlement_currencies: string[];
  // Whole-book pools so the FE doesn't derive them from a windowed page: symbols/
  // currencies feed the sidebar dropdowns; settlement_ccy_totals feeds the pill.
  symbols: string[];
  currencies: string[];
  settlement_ccy_totals: SettlementCcyTotal[];
  /** LR-700: per-term-type counts for the Term pill, same whole-slice basis. */
  term_type_totals: TermTypeTotal[];
  /** LR-469: per-column, per-currency OPEN-book subtotals for the blotter footer /
   *  CSV totals (keyed by blotter column id -> {ccy: decimal string}). Whole-book,
   *  so the footer no longer totals only the loaded window (LR-546). Only the
   *  columns derivable server-side without the rate engine / COB prices are
   *  present; daily_earnings / closing_cash / cob_* stay client-side. */
  column_totals: Record<string, MoneySummary>;
  by_counterparty: CounterpartySummary[];
}

/**
 * Paginated response for position list
 */
export interface PaginatedTradeList {
  count: number;
  next: string | null;
  previous: string | null;
  results: PositionListItem[];
}

/**
 * Create Position Request - matches backend PositionCreate schema
 */
export interface CreateTradeRequest {
  // Security Information
  symbol_id: string;
  description: string;
  sedol?: string;
  isin: string;
  security_type?: SecurityType;
  country?: string;
  currency: string;
  
  // Account Information
  booking_center: string;
  custodian_id?: number;
  custodian_account: string;
  counterparty_account: string;
  counterparty_name: string;
  
  // Agreement & Trade Type
  agreement: FrameworkAgreement;
  trade_type: TradeType;
  collateral_type?: CollateralType;
  /** LR-586: required for cash collateral, rejected for the other types. */
  cash_pool?: number | null;
  
  // Position Details
  quantity: number;
  available_quantity?: number;
  gross_price: string;
  current_market_price?: string;
  margin_bps?: number;

  // Rate Information
  rate_entry_mode?: RateEntryMode;
  rate_type?: RateType;
  benchmark_name?: BenchmarkRate;
  /** LR-631: omitted for a pooled GMSLA fee trade — the server stamps it from
   *  the cash pool, which is the only place that rate exists. Required for every
   *  other shape; the API answers a miss with a field error naming the pool. */
  benchmark_rate?: string;
  benchmark_locked?: boolean;
  benchmark_lock_date?: string | null;
  spread_bps?: number | null;
  fee_percent?: string | null;
  day_count_convention?: DayCountConvention | null;
  
  // Trade Details
  trade_reason: string;
  trade_reason_comment?: string;
  dividend_passthrough_percent?: string;
  
  // Duration
  term_type?: TermType;
  trade_date: string;
  settlement_date: string;
  maturity_date: string | null;
  notice_period_days?: number | null;
  
  // Settlement
  delivery_type?: DeliveryType;
  settlement_venue: SettlementVenue;
  cash_settlement_place?: number | null;
  securities_settlement_place?: number | null;
  settlement_currency: string;
  fx_rate?: string;

  // Non-cash collateral
  collateral_isin?: string;
  collateral_symbol_id?: string;
  collateral_quantity?: string;
  collateral_cob_price?: string;
  collateral_current_market_price?: string;
  collateral_percent?: string;
  pre_delivery_party?: PreDeliveryParty | '';
  collateral_settlement_place?: number | null;

  // Status & Notes
  status?: TradeStatus;
  created_by?: string;
  notes?: string;
}

/**
 * Create Position Response
 */
export interface CreateTradeResponse {
  id: number;
  status: TradeStatus;
  symbol_id: string;
  counterparty_name: string;
  quantity: number;
  trade_date: string;
}

/**
 * Private settings from backend
 */
export interface PrivateSettings {
  exante_custodian_accounts: {
    [key in 'DTC' | 'EUROCLEAR']: {
      lending: string;
      borrowing: string;
      repo: string;
      reverse_repo: string;
    };
  };
}

/**
 * Agreement - for agreement selection
 */
export interface Agreement {
  id: string;
  type: FrameworkAgreement;
  status: AgreementStatus;
  valid_from: string;
  valid_to?: string;
  allowed_trade_types: TradeType[];
  default_settlement_venue?: SettlementVenue;
  default_delivery_type?: DeliveryType;
  default_margin_rate?: number;
  default_dividend_pass_through?: number;
}

/**
 * Local filters for client-side filtering
 */
export interface LocalTradeFilters {
  // LR-497: widened to EffectiveTradeStatus so the two derived buckets
  // (partially_settled / pending_partial_settlement) are selectable.
  statuses: EffectiveTradeStatus[];
  counterparties: string[];
  /** Security symbols (symbol_id, e.g. "AAPL.NASDAQ") to filter on (LR-469). */
  symbols: string[];
  tradeTypes: TradeType[];
  collateralTypes: CollateralType[];
  agreementTypes: FrameworkAgreement[];
  termTypes: TermType[];
  legalEntities: string[];
  accountIds: string[];
  /** Security-currency filter (sidebar "Security Currency" section). */
  currencies: string[];
  /** Effective settlement-currency filter — drives the summary-bar currency
   *  pill (LR-474). Distinct from `currencies` by design (D3: two filters). */
  settlementCurrencies: string[];
  tradeDateFrom: string | null;
  tradeDateTo: string | null;
  settlementDateFrom: string | null;
  settlementDateTo: string | null;
  maturityDateFrom: string | null;
  maturityDateTo: string | null;
  quantityMin: number | null;
  quantityMax: number | null;
  contractValueMin: number | null;
  contractValueMax: number | null;
  marginMin: number | null;
  marginMax: number | null;
  grossPriceMin: number | null;
  grossPriceMax: number | null;
  dailyEarningsMin: number | null;
  dailyEarningsMax: number | null;
  createdAtFrom: string | null;
  createdAtTo: string | null;
  updatedAtFrom: string | null;
  updatedAtTo: string | null;
  rateMin: number | null;
  rateMax: number | null;
}

/**
 * Utility type converters
 */
export const TradeTypeConverters = {
  toBackend: (type: TradeType): TradeType => type,
  toFrontend: (type: TradeType): TradeType => type,
  getDisplay: (type: TradeType): string => {
    const displays: Record<TradeType, string> = {
      lend: 'Stock Lending',
      borrow: 'Stock Borrowing',
      repo: 'Repo',
      reverse_repo: 'Reverse Repo',
    };
    return displays[type] || type;
  },
};

export const AgreementConverters = {
  toBackend: (agreement: FrameworkAgreement): FrameworkAgreement => agreement,
  toFrontend: (agreement: FrameworkAgreement): FrameworkAgreement => agreement,
  getDisplay: (agreement: FrameworkAgreement): string => {
    const displays: Record<FrameworkAgreement, string> = {
      gmsla: 'GMSLA',
      gmra: 'GMRA',
    };
    return displays[agreement] || agreement;
  },
};

/**
 * Convert rate between decimal and percentage
 */
export const rateToDecimal = (percent: number): string => (percent / 100).toFixed(4);
export const decimalToRate = (decimal: string): number => parseFloat(decimal) * 100;

/**
 * Custodian bank registry item
 */
export interface CustodianItem {
  id: number;
  name: string;
  short_code: string;
  account_number: string;
  is_active: boolean;
}

/**
 * Holiday calendar item
 */
export interface HolidayItem {
  id: number;
  date: string;
  country: string;
  name: string;
  is_active: boolean;
}

/**
 * Response from holiday check endpoint
 */
export interface HolidayCheckResponse {
  is_holiday: boolean;
  holidays: HolidayItem[];
}

/**
 * Transform PositionListItem to TradeItem for UI compatibility
 */
export const transformPositionToTradeItem = (position: PositionListItem): TradeItem => ({
  id: position.id,
  status: position.status,
  status_display: position.status_display,
  symbol_id: position.symbol_id,
  description: position.description,
  isin: position.isin,
  counterparty: position.counterparty_name,
  counterpartyId: position.counterparty_id,
  agreement: position.agreement,
  agreement_display: position.agreement_display,
  trade_type: position.trade_type,
  trade_type_display: position.trade_type_display,
  collateral_type: position.collateral_type,
  // LR-586: the serializer emits both, so the mapper must too - a TradeItem
  // field no producer fills is a column that renders empty with type-check green.
  cash_pool: position.cash_pool,
  cash_pool_name: position.cash_pool_name,
  quantity: position.quantity,
  // LR-388: pending partial recall/return + the resulting remaining markable shape.
  pending_recall_quantity: position.pending_recall_quantity,
  remaining_quantity: position.remaining_quantity,
  // LR-703: the overdue-recall row signal reads this — a field no producer fills is
  // a signal that silently never fires with type-check green.
  pending_recall_settlement_date: position.pending_recall_settlement_date ?? null,
  gross_price: parseFloat(position.gross_price),
  gross_price_raw: position.gross_price,
  cob_price: position.cob_price != null ? parseFloat(position.cob_price) : null,
  cob_price_raw: position.cob_price ?? null,
  cob_fx_rate: position.cob_fx_rate != null ? parseFloat(position.cob_fx_rate) : null,
  // LR-521: COB margin price + gross/margin values (+ settlement-ccy twins).
  cob_margin_price:
    position.cob_margin_price != null ? parseFloat(position.cob_margin_price) : null,
  cob_margin_price_converted:
    position.cob_margin_price_converted != null ? parseFloat(position.cob_margin_price_converted) : null,
  cob_gross_value:
    position.cob_gross_value != null ? parseFloat(position.cob_gross_value) : null,
  cob_gross_value_converted:
    position.cob_gross_value_converted != null ? parseFloat(position.cob_gross_value_converted) : null,
  cob_margin_value:
    position.cob_margin_value != null ? parseFloat(position.cob_margin_value) : null,
  cob_margin_value_converted:
    position.cob_margin_value_converted != null ? parseFloat(position.cob_margin_value_converted) : null,
  current_market_price: position.current_market_price ? parseFloat(position.current_market_price) : null,
  current_market_price_updated_at: position.current_market_price_updated_at ?? null,
  margin_price: position.margin_price ? parseFloat(position.margin_price) : null,
  contract_value: position.contract_value ? parseFloat(position.contract_value) : 0,
  margin: parseFloat(position.margin),
  rate_entry_mode: position.rate_entry_mode,
  // LR-539: rate_type + benchmark_rate feed the bulk re-rate R7 gate (fixed → no
  // spread) and the preview build-up. The list serializer emits both; drop them
  // here and every TradeItem carries rate_type===undefined → a fixed-rate row is
  // mistaken for floating (spread offered → backend R7 400 → whole batch rolls
  // back) and rebate previews render "—". Mirror the sibling decimal coercion.
  rate_type: position.rate_type,
  benchmark_rate: position.benchmark_rate != null ? parseFloat(position.benchmark_rate) : null,
  benchmark_name: position.benchmark_name,
  total_rate: position.total_rate ? parseFloat(position.total_rate) : null,
  benchmark_follows_fixing: position.benchmark_follows_fixing,
  // LR-628: null (not 0) when absent — the blotter falls back to the booked
  // figure via `??`, and a coerced 0 would read as a real 0 % rate.
  effective_benchmark_rate:
    position.effective_benchmark_rate != null
      ? parseFloat(position.effective_benchmark_rate)
      : null,
  effective_total_rate:
    position.effective_total_rate != null ? parseFloat(position.effective_total_rate) : null,
  effective_daily_earnings:
    position.effective_daily_earnings != null
      ? parseFloat(position.effective_daily_earnings)
      : null,
  effective_cost_of_collateral:
    position.effective_cost_of_collateral != null
      ? parseFloat(position.effective_cost_of_collateral)
      : null,
  effective_net_amount:
    position.effective_net_amount != null ? parseFloat(position.effective_net_amount) : null,
  cost_of_collateral:
    position.cost_of_collateral != null ? parseFloat(position.cost_of_collateral) : null,
  fee_income: position.fee_income != null ? parseFloat(position.fee_income) : null,
  net_amount: position.net_amount != null ? parseFloat(position.net_amount) : null,
  effective_daily_earnings_converted:
    position.effective_daily_earnings_converted != null
      ? parseFloat(position.effective_daily_earnings_converted)
      : null,
  spread_bps: position.spread_bps,
  fee_percent: position.fee_percent ? parseFloat(position.fee_percent) : null,
  // Guard like every sibling field below (`X ? parseFloat(X) : null`). A bare
  // parseFloat(null/'') yields NaN, which the Trades-list cell then renders as
  // the literal "NaN USD" on a freshly-created trade (QA finding D).
  daily_earnings: position.daily_earnings ? parseFloat(position.daily_earnings) : null,
  trade_date: position.trade_date,
  settlement_date: position.settlement_date,
  maturity_date: position.maturity_date,
  notice_period_days: position.notice_period_days,
  notice_served: position.notice_served,
  notice_served_date: position.notice_served_date,
  notice_served_by: position.notice_served_by,
  actual_settlement_date: position.actual_settlement_date,
  delivery_type: position.delivery_type,
  settlement_venue: position.settlement_venue,
  cash_settlement_place: position.cash_settlement_place,
  securities_settlement_place: position.securities_settlement_place,
  cash_settlement_place_name: position.cash_settlement_place_name,
  securities_settlement_place_name: position.securities_settlement_place_name,
  cash_settled: position.cash_settled,
  securities_settled: position.securities_settled,
  cash_settled_at: position.cash_settled_at,
  securities_settled_at: position.securities_settled_at,
  settlement_method: position.settlement_method,
  custodian_name: position.custodian_name,
  custodian_account: position.custodian_account,
  counterparty_account: position.counterparty_account,
  booking_center: position.booking_center,
  currency: position.currency,
  created_at: position.created_at,
  updated_at: position.updated_at,
  created_by: undefined,
  term_type: position.term_type,
  term_type_display: position.term_type_display,
  collateral_isin: position.collateral_isin,
  collateral_symbol_id: position.collateral_symbol_id,
  collateral_quantity: position.collateral_quantity ? parseFloat(position.collateral_quantity) : null,
  collateral_percent: position.collateral_percent ? parseFloat(position.collateral_percent) : undefined,
  collateral_value: position.collateral_value ? parseFloat(position.collateral_value) : null,
  pre_delivery_party: position.pre_delivery_party,
  collateral_settlement_place: position.collateral_settlement_place ?? null,
  collateral_settlement_place_name: position.collateral_settlement_place_name ?? null,
  last_marked_price: position.last_marked_price != null
    ? parseFloat(position.last_marked_price)
    : null,
  last_marked_price_raw: position.last_marked_price ?? null,
  latest_mark_price: position.latest_mark_price != null
    ? parseFloat(position.latest_mark_price)
    : null,
  latest_mark_price_raw: position.latest_mark_price ?? null,
  // LR-408: BO failure tracking.
  bo_retry_count: position.bo_retry_count,
  last_bo_error: position.last_bo_error,
  // LR-711: same warning as `pending_recall_settlement_date` above — a field no
  // producer fills is a signal that silently never fires with type-check green.
  bo_posting: position.bo_posting ?? null,
  // LR-349: closing cash (was previously unmapped → blank column on live data),
  // plus the settlement-currency conversion for cross-currency trades.
  closing_cash: position.closing_cash != null ? parseFloat(position.closing_cash) : null,
  closing_cash_projected: position.closing_cash_projected,
  closing_cash_converted:
    position.closing_cash_converted != null ? parseFloat(position.closing_cash_converted) : null,
  // LR-459: settlement-currency money metrics for cross-currency trades.
  margin_price_converted:
    position.margin_price_converted != null ? parseFloat(position.margin_price_converted) : null,
  contract_value_converted:
    position.contract_value_converted != null ? parseFloat(position.contract_value_converted) : null,
  daily_earnings_converted:
    position.daily_earnings_converted != null ? parseFloat(position.daily_earnings_converted) : null,
  // LR-522: Trade Gross Value (gross_price × qty) + settlement twin.
  trade_gross_value:
    position.trade_gross_value != null ? parseFloat(position.trade_gross_value) : null,
  trade_gross_value_converted:
    position.trade_gross_value_converted != null
      ? parseFloat(position.trade_gross_value_converted)
      : null,
  // LR-476/522: marked (post-MtM) display twins; null until the position is first
  // marked (blank Last Marked stage), so these are null for never-marked / repo.
  marked_margin_price:
    position.marked_margin_price != null ? parseFloat(position.marked_margin_price) : null,
  marked_margin_price_converted:
    position.marked_margin_price_converted != null
      ? parseFloat(position.marked_margin_price_converted)
      : null,
  collateral_to_return:
    position.collateral_to_return != null ? parseFloat(position.collateral_to_return) : null,
  marked_contract_value:
    position.marked_contract_value != null ? parseFloat(position.marked_contract_value) : null,
  marked_contract_value_converted:
    position.marked_contract_value_converted != null
      ? parseFloat(position.marked_contract_value_converted)
      : null,
  // LR-522: Last Marked Gross Value (latest_mark_price × qty; null until marked) + twin.
  last_marked_gross_value:
    position.last_marked_gross_value != null ? parseFloat(position.last_marked_gross_value) : null,
  last_marked_gross_value_converted:
    position.last_marked_gross_value_converted != null
      ? parseFloat(position.last_marked_gross_value_converted)
      : null,
  settlement_currency: position.settlement_currency,
  // LR-474: booked FX rate (security → settlement) for converting metrics
  // without a *_converted twin (Total Value, exposure).
  fx_rate: position.fx_rate != null ? parseFloat(position.fx_rate) : null,
});
